import { useState } from "react";
import { faArrowRight, faArrowsRepeat, faCalendarClock } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";
import { UISubscriptionStatus } from "common/types";
import { toDate } from "common/utils/dates";
import Spinner from "common/components/Spinner";
import { pluralized } from "common/utils/strings";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClientQuery } from "client/accounts/hooks";
import { useFeatures } from "client/clinics/hooks";
import SubscriptionStatus from "client/subscriptions/components/SubscriptionStatus";
import { ListSubscription } from "client/subscriptions/types";
import TextChip from "client/common/components/content/TextChip";
import TextButton from "client/common/components/buttons/TextButton";
import { useSubscriptionsQuery } from "client/subscriptions/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import {
  useSubscriptionCancelDialog,
  useSubscriptionFrequencyUpdateDrawer,
  useSubscriptionNextOrderDateUpdateDrawer,
} from "client/subscriptions/stackables";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { CanceledOrder, IOrder } from "../types";
import { useOrderCancelMutation, useOrderQuery } from "../hooks";

import styles from "../styles/OrderCancelationDrawer.module.less";

type SubscriptionActionProps = {
  onClick: () => void;
  disabled?: boolean;
  action?: string;
  children: React.ReactNode;
};

const SubscriptionAction = ({
  onClick,
  disabled = false,
  action = "Update",
  children,
}: SubscriptionActionProps) => {
  return (
    <div className={styles.subscriptionAction}>
      <div>{children}</div>
      {!disabled && (
        <TextButton className={styles.link} onClick={onClick}>
          {action}
          <Icon className={styles.arrow} icon={faArrowRight} fixedWidth={true} />
        </TextButton>
      )}
    </div>
  );
};

type SubscriptionProps = {
  subscription: ListSubscription;
  isLoading?: boolean;
};

const Subscription = ({ subscription, isLoading = false }: SubscriptionProps) => {
  const isCanceled = subscription.visible_status === UISubscriptionStatus.Canceled;
  const isActive = subscription.visible_status === UISubscriptionStatus.Active;
  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;

  const { open: openSubscriptionFrequencyDrawer } = useSubscriptionFrequencyUpdateDrawer();
  const { open: openSubscriptionCancelDialog } = useSubscriptionCancelDialog();
  const { open: openSubscriptionNextOrderDateUpdateDrawer } =
    useSubscriptionNextOrderDateUpdateDrawer();

  const getNextChipLabel = () => {
    if (!isEdbEnabled) {
      return subscription.next_order_date
        ? `Next order on ${toDate(subscription.next_order_date)}`
        : "Next order pending";
    }
    return subscription.expected_delivery_by
      ? `Next est. delivery ${toDate(subscription.expected_delivery_by)}`
      : "Next est. delivery TBD";
  };

  return (
    <div className={styles.item}>
      <CatalogImage
        image={subscription.item.image}
        size="thumbnail"
        alt={subscription.item.full_name}
        className={styles.image}
      />
      <div className={styles.content}>
        <div className={styles.nameContainer}>
          <div className={styles.name}>{subscription.item.product.product_line.name}</div>
          <div className={styles.description}>
            {subscription.item.product.name} · {subscription.item.name}
            {subscription.flavors_display && ` · ${subscription.flavors_display}`} ·{" "}
            {subscription.patient && subscription.patient.name_display}
          </div>
        </div>
        <SubscriptionAction
          onClick={() => openSubscriptionCancelDialog({ subscription })}
          disabled={isCanceled}
          action="Cancel Autoship"
        >
          <SubscriptionStatus subscription={subscription} />
        </SubscriptionAction>
        {isActive && (
          <>
            <SubscriptionAction
              onClick={() => openSubscriptionFrequencyDrawer({ id: subscription.id })}
            >
              <TextChip variant="grey" className={styles.chip}>
                <Icon icon={faArrowsRepeat} fixedWidth={true} />
                <div>Ships {subscription.active_frequency.display.toLowerCase()}</div>
              </TextChip>
            </SubscriptionAction>
            <SubscriptionAction
              onClick={() =>
                openSubscriptionNextOrderDateUpdateDrawer({
                  id: subscription.id,
                })
              }
            >
              <TextChip variant="grey" className={styles.chip}>
                <Icon icon={faCalendarClock} fixedWidth={true} />
                <div>{getNextChipLabel()}</div>
              </TextChip>
            </SubscriptionAction>
          </>
        )}
      </div>
      <Spinner className={cx(!isLoading && styles.hidden)} />
    </div>
  );
};

type ConfirmationStepProps = {
  onClose: () => void;
  canceledOrder: CanceledOrder;
};

const ConfirmationStep = ({ onClose, canceledOrder }: ConfirmationStepProps) => {
  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;
  const { data: client, isLoading: clientLoading, error: clientError } = useClientQuery();
  const subscriptionIds = [
    ...canceledOrder.newly_canceled_subscription_ids,
    ...canceledOrder.unaffected_subscription_ids,
  ];
  const {
    data: subscriptions = [],
    isLoading: areSubscriptionsLoading,
    isRefetching: areSubscriptionsRefetching,
    error: subscriptionsError,
  } = useSubscriptionsQuery({ ids: subscriptionIds }, { enabled: subscriptionIds.length > 0 });

  if (clientLoading || areSubscriptionsLoading) {
    return <DrawerLayout.Loading />;
  }

  if (clientError || !client) {
    return <DrawerLayout.FetchError entityName="client" error={clientError} />;
  }

  if (subscriptionsError || !subscriptions) {
    return (
      <DrawerLayout.FetchError entityName="subscriptions" isPlural={true} error={clientError} />
    );
  }

  const newlyCanceledSubscriptions = subscriptions.filter((s) =>
    canceledOrder.newly_canceled_subscription_ids.includes(s.id),
  );
  const activeSubscriptions = subscriptions.filter(
    (s) =>
      canceledOrder.unaffected_subscription_ids.includes(s.id) &&
      s.visible_status === UISubscriptionStatus.Active,
  );
  const successText = "Your order has been successfully canceled. ";

  const getUpcomingBoldText = () => {
    const firstSub = activeSubscriptions[0];
    if (!isEdbEnabled) {
      return firstSub.next_order_date
        ? `next order will be placed on ${toDate(firstSub.next_order_date)}`
        : "next order will be placed soon";
    }
    return firstSub.expected_delivery_by
      ? `next estimated delivery is ${toDate(firstSub.expected_delivery_by)}`
      : "next estimated delivery is being scheduled";
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={
          activeSubscriptions.length
            ? pluralized(activeSubscriptions.length, "Update Autoship")
            : "Cancel order"
        }
        onClose={onClose}
      />
      <DrawerLayout.Content>
        {!activeSubscriptions.length && !newlyCanceledSubscriptions.length && (
          <div className={styles.body}>{successText}</div>
        )}
        {!!activeSubscriptions.length && (
          <div className={styles.section}>
            <div className={styles.body}>
              {successText}
              {activeSubscriptions.length === 1 ? (
                <span>
                  You have an existing Autoship for your canceled item. Your{" "}
                  <span className={styles.bold}>{getUpcomingBoldText()}</span>. Adjust your Autoship
                  below, if needed.
                </span>
              ) : (
                <span>
                  You have existing Autoships for your canceled items and will{" "}
                  <span className={styles.bold}>receive your next order soon</span>. Adjust your
                  Autoships below if needed.
                </span>
              )}
            </div>
            <div className={styles.items}>
              {activeSubscriptions.map((s) => (
                <Subscription key={s.id} subscription={s} isLoading={areSubscriptionsRefetching} />
              ))}
            </div>
          </div>
        )}
        {!!newlyCanceledSubscriptions.length && (
          <div className={styles.section}>
            <div className={styles.body}>
              {!activeSubscriptions.length && successText}
              {newlyCanceledSubscriptions.length === 1
                ? "This first-time Autoship has"
                : "These first-time Autoships have"}{" "}
              been canceled.
            </div>
            <div className={styles.items}>
              {newlyCanceledSubscriptions.map((s) => (
                <Subscription key={s.id} subscription={s} isLoading={areSubscriptionsRefetching} />
              ))}
            </div>
            {client?.first_subscription_eligible_for_clinic && (
              <div className={styles.body}>
                Your first autoship discount will be applied when you create your next autoship.
              </div>
            )}
          </div>
        )}
        <DrawerLayout.Actions>
          <DrawerLayout.Actions.Button onClick={onClose}>
            I&apos;m all set
          </DrawerLayout.Actions.Button>
        </DrawerLayout.Actions>
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

type CancelationStepProps = {
  onNext: (order: CanceledOrder) => void;
  onClose: () => void;
  order: IOrder;
};

const CancelationStep = (props: CancelationStepProps) => {
  const { order, onClose, onNext } = props;

  const {
    mutate: cancelOrder,
    isPending: isCanceling,
    error: cancelError,
  } = useOrderCancelMutation(order.id, { onSuccess: onNext });

  const orderItems = order.order_items.filter((oi) => oi.is_user_cancelable);

  if (!orderItems.length) {
    return <DrawerLayout.Error />;
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Cancel order" onClose={onClose} />
      <DrawerLayout.Content>
        {cancelError && <FetchErrorAlert error={cancelError} />}
        <div className={styles.body}>Are you sure you want to cancel this order?</div>
        <div className={styles.section}>
          <div className={styles.body}>
            {orderItems.length === 1 ? "This item" : "These items"} will be canceled:
          </div>
          <div className={styles.items}>
            {orderItems.map((oi) => (
              <div key={oi.id} className={styles.item}>
                <CatalogImage
                  image={oi.item.image}
                  size="thumbnail"
                  alt={oi.item.full_name}
                  className={styles.image}
                />
                <div className={styles.nameContainer}>
                  <div className={styles.name}>{oi.item.product.product_line.name}</div>
                  <div className={styles.description}>
                    {oi.item.product.name} · {oi.item.name}
                    {oi.flavors_display && ` · ${oi.flavors_display}`} ·{" "}
                    {oi.patient && oi.patient.name_display}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.body}>
          The pending charge on your payment method will be removed shortly.
        </div>
        <DrawerLayout.Actions>
          <DrawerLayout.Actions.Button variant="secondary" disabled={isCanceling} onClick={onClose}>
            Never mind
          </DrawerLayout.Actions.Button>
          <DrawerLayout.Actions.Button isLoading={isCanceling} onClick={() => cancelOrder({})}>
            Cancel order
          </DrawerLayout.Actions.Button>
        </DrawerLayout.Actions>
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export type OrderCancelationDrawerProps = {
  orderId: number;
};

// TODO test me
const OrderCancelationDrawer = ({ orderId }: OrderCancelationDrawerProps) => {
  const { close } = useCurrentDrawer();
  const [canceledOrder, setCanceledOrder] = useState<CanceledOrder | null>(null);
  const { data: order, isLoading, error } = useOrderQuery(orderId, { enabled: !!orderId });

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (error || !order) {
    return <DrawerLayout.FetchError error={error} entityName="order" />;
  }

  return !canceledOrder ? (
    <CancelationStep order={order} onClose={close} onNext={(o) => setCanceledOrder(o)} />
  ) : (
    <ConfirmationStep canceledOrder={canceledOrder} onClose={close} />
  );
};

export default OrderCancelationDrawer;
